import { useState, useEffect, useMemo } from 'react'
import { trackEvent } from '@/utils/analytics'
import { motion } from 'framer-motion'
import {
  XIcon,
  BarChart3,
  FileText,
  ArrowLeft,
  Search,
  CheckCircleIcon,
  Trash2,
  ChevronLeft,
  ExternalLink,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type {
  Node,
  Edge,
  SymbolNode,
  TagNode,
  ConnectionNode,
} from '@/service/graph'
import { NodeType } from '@/types/graph'
import { Button } from '@/components/ui/button'
import { parseName } from '@/service/graph'
import { useTranslation } from '@/i18n'
import { colors } from '@/styles/colors'
import { useTaxonomy } from './TaxonomyContext'
import { convertNodeType, nodeTypeChangeFor } from './classificationType'

interface ProcessedGraphData {
  symbols: Record<string, SymbolNode[]>
  tags: TagNode[]
  instruments?: SymbolNode[] // Instruments are SymbolNodes with is_instrument: true
  connections: ConnectionNode[]
}

interface DetailsFlyoutProps {
  node: Node | undefined
  updateNode: (node: Node) => void
  deleteNode?: (nodeId: string) => void
  deleteEdgeById?: (edgeId: string) => void
  addEdge?: (edge: { id: string; fromNodeId: string; toNodeId: string }) => void
  currentState?: { nodes: Map<string, Node>; edges: Map<string, Edge> }
  isOpen?: boolean
  onClose?: () => void
  showReviewList?: boolean
  processedGraphData?: ProcessedGraphData
  onItemClick?: (nodeId: string) => void
  onBackToReview?: () => void
  isFromCanvas?: boolean
  isFromReviewFlow?: boolean
  onCategoryClick?: () => void
  /** When the node is a drawing_to_drawing_connector, called when user clicks "Go to diagram" */
  onGoToDrawing?: (label: string, extraProperties?: Record<string, string>) => void
  /** When true, uses wider panel for board view */
  isReadOnly?: boolean
}

type SelectedCategory = {
  type: 'symbols' | 'tags' | 'instruments' | 'connections'
  name: string
  items: (SymbolNode | TagNode | ConnectionNode)[]
} | null

/** True if the symbol is a drawing-to-drawing connector (by category, symbol_type, or name). */
function isDrawingConnector(symbol: SymbolNode): boolean {
  const cat = (symbol.category ?? '').toLowerCase()
  const typeStr = (symbol.symbol_type ?? '').toLowerCase()
  const nameStr = (symbol.name ?? '').toLowerCase()
  return (
    cat === 'drawing_to_drawing_connector' ||
    typeStr.includes('drawing_to_drawing_connector') ||
    nameStr.includes('drawing_to_drawing_connector')
  )
}

const DetailsFlyout = ({
  node,
  updateNode,
  deleteNode,
  deleteEdgeById,
  addEdge,
  currentState,
  isOpen: propIsOpen,
  onClose,
  showReviewList = false,
  processedGraphData,
  onItemClick,
  onBackToReview,
  isFromCanvas = false,
  isFromReviewFlow = false,
  onCategoryClick,
  onGoToDrawing,
  isReadOnly = false,
}: DetailsFlyoutProps) => {
  const { t } = useTranslation()
  const taxonomy = useTaxonomy()
  const [isOpen, setIsOpen] = useState(propIsOpen ?? false)
  const [formState, setFormState] = useState<Node | undefined>(node)

  // 3-level classification state — seeded from the node on every node change
  const [classLevelI, setClassLevelI] = useState<string>('Equipment')
  const [classLevelII, setClassLevelII] = useState<string>('')
  const [classLevelIII, setClassLevelIII] = useState<string>('')

  const [selectedCategory, setSelectedCategory] =
    useState<SelectedCategory>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [categorySearchQuery, setCategorySearchQuery] = useState('')
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
  const [showAssociationModal, setShowAssociationModal] = useState(false)
  const [associationSearchQuery, setAssociationSearchQuery] = useState('')

  useEffect(() => {
    if (propIsOpen !== undefined) {
      setIsOpen(propIsOpen)
    }
  }, [propIsOpen])

  useEffect(() => {
    setFormState(node)
    if (node) {
      setIsOpen(true)
      // Prefer previously-saved classification; fall back to heuristic for AI-detected nodes
      if (node.pnid_level_i) {
        setClassLevelI(node.pnid_level_i)
        setClassLevelII(node.pnid_level_ii ?? '')
        setClassLevelIII(node.pnid_level_iii ?? '')
      } else {
        const category = node.type === 'symbol' ? (node as SymbolNode).category : undefined
        const resolved = taxonomy.resolveClassification(node.type as 'symbol' | 'tag' | 'connection', category)
        setClassLevelI(resolved.levelI)
        setClassLevelII(resolved.levelII)
        setClassLevelIII(resolved.levelIII)
      }
    }
  }, [node, taxonomy])

  // Update selectedCategory when processedGraphData changes (e.g., after approving a node)
  useEffect(() => {
    if (selectedCategory && processedGraphData) {
      let updatedItems: (
        | SymbolNode
        | TagNode
        | ConnectionNode
      )[] = []

      switch (selectedCategory.type) {
        case 'symbols': {
          // Find the symbol category and get updated items
          const symbolCategory = Object.entries(
            processedGraphData.symbols
          ).find(([name]) => parseName(name).label === selectedCategory.name)
          if (symbolCategory) {
            updatedItems = symbolCategory[1]
          }
          break
        }
        case 'tags':
          updatedItems = processedGraphData.tags
          break
        case 'instruments':
          updatedItems = processedGraphData.instruments || []
          break
        case 'connections':
          updatedItems = processedGraphData.connections
          break
      }

      if (updatedItems.length > 0) {
        setSelectedCategory({
          ...selectedCategory,
          items: updatedItems,
        })
      }
    }
  }, [processedGraphData]) // Remove selectedCategory from dependencies to prevent interference

  const handleClose = () => {
    setIsOpen(false)
    setSelectedCategory(null)
    onClose?.()
  }

  const handleCategoryClick = (
    type: 'symbols' | 'tags' | 'instruments' | 'connections',
    name: string,
    items: (SymbolNode | TagNode | ConnectionNode)[]
  ) => {
    setSelectedCategory({ type, name, items })
    onCategoryClick?.() // Notify parent that we're entering category view
  }

  const handleBackToCategory = () => {
    setSelectedCategory(null)
    setSearchQuery('') // Clear search when going back to main list
    setCategorySearchQuery('') // Clear category search
    setSelectedItems(new Set()) // Clear selections
    // When selectedCategory is null, it will show the main review list
  }

  const handleItemSelection = (itemId: string) => {
    const newSelectedItems = new Set(selectedItems)
    if (newSelectedItems.has(itemId)) {
      newSelectedItems.delete(itemId)
    } else {
      newSelectedItems.add(itemId)
    }
    setSelectedItems(newSelectedItems)
  }

  const handleSelectAll = () => {
    if (selectedItems.size === getFilteredCategoryItems().length) {
      setSelectedItems(new Set())
    } else {
      setSelectedItems(
        new Set(getFilteredCategoryItems().map((item) => item.id))
      )
    }
  }

  const handleApproveSelected = () => {
    const count = selectedItems.size
    selectedItems.forEach((itemId) => {
      const item = getFilteredCategoryItems().find((i) => i.id === itemId)
      if (item) {
        const updatedItem = { ...item, confidence: 1.0 }
        updateNode(updatedItem)
      }
    })
    trackEvent('graph-entity_approved', { entity_count: count })
    setSelectedItems(new Set())
  }

  const handleDeleteSelected = () => {
    if (!deleteNode || !deleteEdgeById || !currentState) return
    
    selectedItems.forEach((itemId) => {
      // Delete any edges that reference this node
      const edgesToDelete: string[] = []
      currentState.edges.forEach((edge, edgeId) => {
        if (edge.fromNodeId === itemId || edge.toNodeId === itemId) {
          edgesToDelete.push(edgeId)
        }
      })
      // Delete all related edges
      edgesToDelete.forEach(edgeId => deleteEdgeById(edgeId))
      // Delete the node itself
      deleteNode(itemId)
    })
    setSelectedItems(new Set())
  }

  const getFilteredCategoryItems = () => {
    if (!selectedCategory) return []

    const query = categorySearchQuery.toLowerCase()
    return selectedCategory.items.filter((item) => {
      if (selectedCategory.type === 'symbols') {
        return `Symbol ${item.id}`.toLowerCase().includes(query)
      } else if (selectedCategory.type === 'tags') {
        return (item as TagNode).content.toLowerCase().includes(query)
      } else if (selectedCategory.type === 'instruments') {
        return `Instrument ${item.id}`.toLowerCase().includes(query)
      } else {
        return `Connection ${item.id?.slice(0, 8)}`
          .toLowerCase()
          .includes(query)
      }
    })
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  // Memoize filtered data to avoid recalculating on every render
  const filteredData = useMemo(() => {
    if (!processedGraphData)
      return { symbols: {}, tags: [], instruments: [], connections: [] }

    const query = searchQuery.toLowerCase()

    // Filter symbols (includes instruments which are now part of symbols)
    const filteredSymbols: Record<string, SymbolNode[]> = {}
    Object.entries(processedGraphData.symbols).forEach(([name, nodes]) => {
      const matchesName = parseName(name).label.toLowerCase().includes(query)
      if (matchesName) {
        filteredSymbols[name] = nodes
      }
    })

    // Filter tags
    const filteredTags = processedGraphData.tags.filter((tag) =>
      tag.content.toLowerCase().includes(query)
    )

    // Filter instruments (guaranteed to return an array)
    const filteredInstruments = (processedGraphData.instruments || []).filter(
      (instrument) => instrument.id.toLowerCase().includes(query)
    )

    // Filter connections
    const filteredConnections = processedGraphData.connections.filter(
      (connection) => connection.id.toLowerCase().includes(query)
    )

    return {
      symbols: filteredSymbols,
      tags: filteredTags,
      instruments: filteredInstruments,
      connections: filteredConnections,
    }
  }, [processedGraphData, searchQuery])

  const handleSave = () => {
    if (formState) {
      // Only persist classification when the user has explicitly selected a
      // subcategory (Level II). Without it the type is ambiguous; saving a
      // partial classification would mark the node as "classified" and prevent
      // the heuristic from running on subsequent loads.
      const classificationFields = classLevelII
        ? { pnid_level_i: classLevelI, pnid_level_ii: classLevelII, pnid_level_iii: classLevelIII }
        : {}
      const updatedNode = {
        ...formState,
        confidence: 1.0,
        ...classificationFields,
      }
      updateNode(updatedNode)

      if (isFromCanvas) {
        handleClose()
      } else {
        onBackToReview?.()
      }
    }
  }

  const handleRemove = () => {
    if (formState?.id && deleteNode) {
      // Delete any edges that reference this node (for all node types)
      if (deleteEdgeById && currentState) {
        // Find all edges where this node is the source or target
        const edgesToDelete: string[] = []
        currentState.edges.forEach((edge, edgeId) => {
          if (edge.fromNodeId === formState.id || edge.toNodeId === formState.id) {
            edgesToDelete.push(edgeId)
          }
        })
        // Delete all related edges
        edgesToDelete.forEach(edgeId => deleteEdgeById(edgeId))
      }
      // Delete the node itself
      deleteNode(formState.id)
      handleClose()
    }
  }

  const handleContentChange = (node: TagNode, newContent: string) => {
    setFormState({
      ...node,
      content: newContent,
    })
  }

  const handleTypeChange = (newType: NodeType) => {
    setFormState(convertNodeType(formState, newType))
  }

  const handleClassLevelIChange = (value: string) => {
    const next = value
    setClassLevelI(next)
    setClassLevelII('')
    setClassLevelIII('')
    // Do NOT resolve the node type here — with an empty Level II we cannot
    // determine the final type (resolveNodeType always returns 'symbol' for
    // empty levelII, which would wrongly wipe a TagNode's content or a
    // ConnectionNode's geometry). Type sync happens in handleClassLevelIIChange
    // once the user explicitly selects a subcategory.
  }

  const handleClassLevelIIChange = (value: string) => {
    setClassLevelII(value)
    setClassLevelIII('')
    // Level II resets the leaf, so resolve without one (connections still resolve
    // on Level II alone; the tag leaf only resolves once Level III is picked).
    const next = nodeTypeChangeFor(classLevelI, value, '', formState?.type, taxonomy.discipline)
    if (next) handleTypeChange(next)
  }

  const handleClassLevelIIIChange = (value: string) => {
    setClassLevelIII(value)
    // The tag leaf lives at Level III (Annotation ▸ Text ▸ Tag), so the node type
    // can only be resolved to 'tag' once the leaf is picked — re-resolve here.
    const next = nodeTypeChangeFor(classLevelI, classLevelII, value, formState?.type, taxonomy.discipline)
    if (next) handleTypeChange(next)
  }

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return { color: colors.green[600] }
    if (confidence >= 0.6) return { color: colors.yellow[600] }
    return { color: colors.red[600] }
  }

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 0.9) return 'High'
    if (confidence >= 0.6) return 'Medium'
    return 'Low'
  }

  // Get related connections for a tag node
  const getRelatedConnections = (tagId: string): ConnectionNode[] => {
    if (!currentState) return []

    const relatedConnectionIds = Array.from(currentState.edges.values()).reduce<Set<string>>((acc, edge) => {
      if (edge.fromNodeId === tagId) {
        const toNode = currentState.nodes.get(edge.toNodeId)
        if (toNode?.type === NodeType.Connection) {
          acc.add(edge.toNodeId)
        }
      } else if (edge.toNodeId === tagId) {
        const fromNode = currentState.nodes.get(edge.fromNodeId)
        if (fromNode?.type === NodeType.Connection) {
          acc.add(edge.fromNodeId)
        }
      }
      return acc
    }, new Set<string>())

    return Array.from(relatedConnectionIds)
      .map((id) => currentState.nodes.get(id) as ConnectionNode)
      .filter(Boolean)
  }

  // Get related tags for a connection node
  const getRelatedTags = (connectionId: string): TagNode[] => {
    if (!currentState) return []

    const relatedTagIds = Array.from(currentState.edges.values()).reduce<Set<string>>((acc, edge) => {
      if (edge.fromNodeId === connectionId) {
        const toNode = currentState.nodes.get(edge.toNodeId)
        if (toNode?.type === NodeType.Tag) {
          acc.add(edge.toNodeId)
        }
      } else if (edge.toNodeId === connectionId) {
        const fromNode = currentState.nodes.get(edge.fromNodeId)
        if (fromNode?.type === NodeType.Tag) {
          acc.add(edge.fromNodeId)
        }
      }
      return acc
    }, new Set<string>())

    return Array.from(relatedTagIds)
      .map((id) => currentState.nodes.get(id) as TagNode)
      .filter(Boolean)
  }

  // Get all connections that are not yet associated with the current tag
  const getAvailableConnections = (): ConnectionNode[] => {
    if (!formState || !currentState) return []
    const relatedConnectionIds = new Set(
      getRelatedConnections(formState.id).map((c) => c.id)
    )

    return Array.from(currentState.nodes.values())
      .filter((node) => node.type === NodeType.Connection && !relatedConnectionIds.has(node.id))
      .filter((node) => {
        const query = associationSearchQuery.toLowerCase()
        return node.id.toLowerCase().includes(query)
      }) as ConnectionNode[]
  }

  // Get all tags that are not yet associated with the current connection
  const getAvailableTags = (): TagNode[] => {
    if (!formState || !currentState) return []
    const relatedTagIds = new Set(
      getRelatedTags(formState.id).map((t) => t.id)
    )

    return Array.from(currentState.nodes.values())
      .filter((node) => node.type === NodeType.Tag && !relatedTagIds.has(node.id))
      .filter((node) => {
        const query = associationSearchQuery.toLowerCase()
        const tagNode = node as TagNode
        return tagNode.content.toLowerCase().includes(query)
      }) as TagNode[]
  }

  const handleAddAssociation = (targetNodeId: string) => {
    if (!formState || !addEdge || !currentState) return

    // Check if an edge already exists between these nodes in either direction
    const edgeExists = Array.from(currentState.edges.values()).some((edge) => 
      (edge.fromNodeId === formState.id && edge.toNodeId === targetNodeId) ||
      (edge.toNodeId === formState.id && edge.fromNodeId === targetNodeId)
    )

    // Only create the edge if it doesn't already exist
    if (!edgeExists) {
      const edgeId = `${formState.id}-${targetNodeId}`
      addEdge({
        id: edgeId,
        fromNodeId: formState.id,
        toNodeId: targetNodeId,
      })
    }

    setShowAssociationModal(false)
    setAssociationSearchQuery('')
  }

  const handleRemoveAssociation = (targetNodeId: string) => {
    if (!formState || !deleteEdgeById || !currentState) return

    // Find and delete the edge connecting these nodes in either direction
    Array.from(currentState.edges.entries())
      .filter(([, edge]) => 
        (edge.fromNodeId === formState.id && edge.toNodeId === targetNodeId) ||
        (edge.toNodeId === formState.id && edge.fromNodeId === targetNodeId)
      )
      .forEach(([edgeId]) => deleteEdgeById(edgeId))
  }

  if (!isOpen) return null

  return (
    <>
      {/* Flyout Panel */}
      <motion.div
        initial={{ x: '100%' }}
        animate={{
          x: isOpen ? 0 : '100%',
        }}
        transition={{ ease: 'easeInOut', duration: 0.3 }}
        className={`absolute top-0 right-0 h-full border-t ${isReadOnly ? 'w-4/12' : 'w-3/12'} shadow-xl z-[60] flex flex-col bg-gray-50 dark:bg-dark-muted border-l border-gray-200 dark:border-dark-border`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 shrink-0 border-b border-gray-200 dark:border-dark-border">
          {showReviewList ? (
            selectedCategory ? (
              <>
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleBackToCategory}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text py-1">
                    {selectedCategory.name}
                  </h3>
                  <span className="text-sm text-gray-500 dark:text-dark-text-muted">
                    {t(
                      'data_catalog:graph_editor.details.headers.selection_count',
                      {
                        selected: selectedItems.size,
                        total: getFilteredCategoryItems().length,
                      }
                    )}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text py-1">
                    {t('data_catalog:graph_editor.details.headers.review_list')}
                  </h3>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={onClose}
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              </>
            )
          ) : (
            <>
              <div className="flex items-center gap-3">
                {(!isFromCanvas || isFromReviewFlow) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={onBackToReview}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                )}
                <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text py-1">
                  {t('data_catalog:graph_editor.details.headers.node_properties')}
                </h3>
              </div>
              {isFromCanvas && !showReviewList && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleClose}
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              )}
            </>
          )}
        </div>

        {/* Content */}
        <div className="flex flex-col min-h-0 flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {showReviewList && processedGraphData ? (
              selectedCategory ? (
                // Category Details View
                <div className="space-y-4">
                  {/* Category Header with Count */}

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleApproveSelected}
                      disabled={selectedItems.size === 0}
                    >
                      <CheckCircleIcon className="w-4 h-4 mr-1" />
                      {t('data_catalog:graph_editor.details.buttons.approve_selected')}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleDeleteSelected}
                      disabled={selectedItems.size === 0}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      {t('data_catalog:graph_editor.details.buttons.delete_selected')}
                    </Button>
                  </div>

                  {/* Items List */}
                  <div>
                    {/* Select All Checkbox */}
                    <div
                      className="flex items-center gap-2 cursor-pointer p-3 bg-gray-100 dark:bg-dark-accent mb-0"
                      onClick={handleSelectAll}
                    >
                      <input
                        type="checkbox"
                        checked={
                          selectedItems.size ===
                            getFilteredCategoryItems().length &&
                          getFilteredCategoryItems().length > 0
                        }
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {selectedCategory.type &&
                          t(
                            `data_catalog:graph_editor.details.select_all_label.${selectedCategory.type}`
                          )}
                      </span>
                    </div>
                    {getFilteredCategoryItems().map((item, index) => (
                      <div
                        key={item.id || index}
                        className={`flex justify-between items-center p-3 hover:bg-gray-100 dark:hover:bg-dark-accent cursor-pointer border-b border-gray-200 dark:border-dark-border ${
                          selectedItems.has(item.id) && 'bg-blue-50 dark:bg-blue-900/20 '
                        }`}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <input
                            type="checkbox"
                            checked={selectedItems.has(item.id)}
                            onChange={() => handleItemSelection(item.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                          />
                          <span
                            className="text-gray-900 dark:text-dark-text font-medium flex-1 cursor-pointer"
                            onClick={() => onItemClick?.(item.id)}
                          >
                              {selectedCategory.type === 'symbols'
                                ? t(
                                    'data_catalog:graph_editor.details.list_items.symbol',
                                    { index: index + 1 }
                                  )
                                : selectedCategory.type === 'tags'
                                ? (item as TagNode).content
                                : selectedCategory.type === 'instruments'
                                ? t(
                                    'data_catalog:graph_editor.details.list_items.instrument',
                                    { index: index + 1 }
                                  )
                                : t(
                                    'data_catalog:graph_editor.details.list_items.connection',
                                    {
                                      id:
                                        item.id?.slice(0, 8) || (index + 1).toString(),
                                    }
                                  )}
                            </span>
                        </div>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            (item.confidence || 0) >= 0.9
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                              : (item.confidence || 0) >= 0.6
                              ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                              : 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300'
                          }`}
                        >
                          {(item.confidence || 0) >= 0.9
                            ? t('data_catalog:graph_editor.details.statuses.approved')
                            : t('data_catalog:graph_editor.details.statuses.pending')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                // Review List Content
                <>
                  {/* Search Box */}
                  <div className="space-y-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
                      <Input
                        type="text"
                        placeholder={t(
                          'data_catalog:graph_editor.details.search.placeholder'
                        )}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  {/* Symbols List */}
                  {Object.keys(filteredData.symbols).length > 0 && (
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900 dark:text-dark-text">
                        {t('data_catalog:graph_editor.details.sections.symbols')}
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(filteredData.symbols).map(
                          ([name, nodes]) => (
                            <div
                              key={name}
                              className="flex justify-between items-center p-1 rounded-md hover:bg-gray-50 dark:hover:bg-dark-muted cursor-pointer"
                              onClick={() =>
                                handleCategoryClick(
                                  'symbols',
                                  parseName(name).label,
                                  nodes
                                )
                              }
                            >
                              <span className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                                {parseName(name).label}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500 dark:text-dark-text-muted text-sm font-medium">
                                  {nodes.length}
                                </span>
                                <ChevronLeft className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}

                  {/* Tags List */}
                  {filteredData.tags.length > 0 && (
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900 dark:text-dark-text">
                        {t('data_catalog:graph_editor.details.sections.tags')}
                      </h4>
                      <div className="space-y-2">
                        {filteredData.tags.map((tag) => (
                          <div
                            key={tag.id}
                            className="flex justify-between items-center p-1 rounded-md hover:bg-gray-50 dark:hover:bg-dark-muted cursor-pointer"
                            onClick={() =>
                              handleCategoryClick(
                                'tags',
                                t('data_catalog:graph_editor.details.sections.tags'),
                                [tag]
                              )
                            }
                          >
                            <span className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                              {tag.content}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500 dark:text-dark-text-muted text-sm font-medium">
                                1
                              </span>
                              <ChevronLeft className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Instruments List */}
                  {filteredData.instruments.length > 0 && (
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900 dark:text-dark-text">
                        {t('data_catalog:graph_editor.details.sections.instruments')}
                      </h4>
                      <div className="space-y-2">
                        {filteredData.instruments.map((instrument) => (
                          <div
                            key={instrument.id}
                            className="flex justify-between items-center p-1 rounded-md hover:bg-gray-50 dark:hover:bg-dark-muted cursor-pointer"
                            onClick={() =>
                              handleCategoryClick(
                                'instruments',
                                instrument.name || 'Instrument',
                                [instrument]
                              )
                            }
                          >
                            <span className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                              {instrument.name}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500 dark:text-dark-text-muted text-sm font-medium">
                                1
                              </span>
                              <ChevronLeft className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Connections List */}
                        {filteredData.connections.length > 0 && (
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900 dark:text-dark-text">
                        {t('data_catalog:graph_editor.details.sections.connections')}
                      </h4>
                      <div className="space-y-2">
                        {filteredData.connections.map((connection) => (
                          <div
                            key={connection.id}
                            className="flex justify-between items-center p-1 rounded-md hover:bg-gray-50 dark:hover:bg-dark-muted cursor-pointer"
                            onClick={() =>
                              handleCategoryClick(
                                'connections',
                                t('data_catalog:graph_editor.details.sections.connections'),
                                [connection]
                              )
                            }
                          >
                            <span className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                              {t(
                                'data_catalog:graph_editor.details.list_items.connection',
                                { id: connection.id.slice(0, 8) }
                              )}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500 dark:text-dark-text-muted text-sm font-medium">
                                1
                              </span>
                              <ChevronLeft className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Empty State */}
                  {Object.keys(filteredData.symbols).length === 0 &&
                    filteredData.tags.length === 0 &&
                    filteredData.instruments.length === 0 &&
                    filteredData.connections.length === 0 && (
                    <div className="text-center py-8">
                      <FileText className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                      <p className="text-gray-500 dark:text-dark-text-muted">
                        {searchQuery
                          ? t('data_catalog:graph_editor.details.empty.no_results')
                          : t('data_catalog:graph_editor.details.empty.no_objects')}
                      </p>
                      <p className="text-gray-400 dark:text-gray-500 text-sm">
                        {searchQuery
                          ? t('data_catalog:graph_editor.details.empty.adjust_search')
                          : t('data_catalog:graph_editor.details.empty.add_nodes')}
                      </p>
                    </div>
                    )}
                </>
              )
            ) : (
              // Node Details Content
              <>
                {/* Node Details */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900 dark:text-dark-text">
                    {t('data_catalog:graph_editor.details.node_details.title')}
                  </h4>

                  <div className="space-y-3">
                    {/* Level I — Category */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('data_catalog:control_panel.level_i.label')}
                      </label>
                      <Select value={classLevelI} onValueChange={handleClassLevelIChange}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('data_catalog:control_panel.level_i.placeholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          {taxonomy.levelIValues.map((val) => (
                            <SelectItem key={val} value={val}>{val}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Level II — Subcategory */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('data_catalog:control_panel.level_ii.label')}
                      </label>
                      <Select value={classLevelII} onValueChange={handleClassLevelIIChange} disabled={!classLevelI}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('data_catalog:control_panel.level_ii.placeholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          {taxonomy.getLevelII(classLevelI).map((val) => (
                            <SelectItem key={val} value={val}>{val}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Level III — Type (always shown, disabled when no options) */}
                    {(() => {
                      const options = classLevelII ? taxonomy.getLevelIII(classLevelI, classLevelII) : []
                      return (
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t('data_catalog:control_panel.level_iii.label')}
                          </label>
                          <Select
                            value={classLevelIII}
                            onValueChange={handleClassLevelIIIChange}
                            disabled={!classLevelII || options.length === 0}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder={
                                classLevelII && options.length === 0
                                  ? 'N/A'
                                  : t('data_catalog:control_panel.level_iii.placeholder')
                              } />
                            </SelectTrigger>
                            <SelectContent>
                              {options.map((val) => (
                                <SelectItem key={val} value={val}>{val}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )
                    })()}

                    {/* Detected Class — read-only, only when the AI has a value */}
                    {formState?.type === NodeType.Symbol && (formState as SymbolNode).symbol_type && (formState as SymbolNode).symbol_type !== 'null' && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('data_catalog:graph_editor.details.node_details.fields.detected_type.label')}
                        </label>
                        <div className="w-full h-9 flex items-center px-3 rounded-md border border-gray-300 dark:border-gray-600 text-sm bg-gray-50 dark:bg-dark-muted text-gray-700 dark:text-gray-300">
                          {(formState as SymbolNode).symbol_type}
                        </div>
                      </div>
                    )}

                    {/* Drawing-to-drawing connector */}
                    {formState?.type === NodeType.Symbol &&
                      isDrawingConnector(formState as SymbolNode) &&
                      onGoToDrawing && (
                      <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-dark-border">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('data_catalog:graph_editor.details.drawing_connector_section')}
                        </label>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full justify-start gap-2"
                          disabled={
                            !((formState as SymbolNode).label ?? '').trim() &&
                            !(formState as SymbolNode).extraProperties?.connected_to_graph_id
                          }
                          onClick={() => {
                            const sym = formState as SymbolNode
                            const label = (sym.label ?? '').trim()
                            if (label || sym.extraProperties?.connected_to_graph_id) {
                              onGoToDrawing(label, sym.extraProperties)
                            }
                          }}
                        >
                          <ExternalLink className="h-4 w-4" />
                          {t('data_catalog:graph_editor.details.go_to_diagram')}
                        </Button>
                      </div>
                    )}

                    {/* Tag — always shown for all node types */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('graph_editor:nodePanel.label', { defaultValue: 'Tag' })}</label>
                      <Input
                        value={
                          formState?.type === NodeType.Tag
                            ? (formState as TagNode).content
                            : (formState?.label ?? '')
                        }
                        onChange={(e) => {
                          if (!formState) return
                          if (formState.type === NodeType.Tag) {
                            handleContentChange(formState as TagNode, e.target.value)
                          } else {
                            setFormState({ ...formState, label: e.target.value })
                          }
                        }}
                        placeholder={t('graph_editor:controlPanel.labelPlaceholder', { defaultValue: 'e.g. V-101, FIC-201, 69TE1120A' })}
                        className="w-full"
                      />
                    </div>

                    {/* Note — always shown for all node types */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('graph_editor:nodePanel.note', { defaultValue: 'Note' })}</label>
                      <Input
                        value={formState?.note ?? ''}
                        onChange={(e) => {
                          if (formState) {
                            setFormState({ ...formState, note: e.target.value })
                          }
                        }}
                        placeholder={t('graph_editor:detailsFlyout.notePlaceholder', { defaultValue: 'Optional note or additional info' })}
                        className="w-full"
                      />
                    </div>

                    {/* Line Number — read-only, shown for connection segments that
                        have been assigned a pipe line number (VISN-140). Visual
                        debugging: click a segment to see which line it belongs to. */}
                    {formState?.type === NodeType.Connection && (formState as ConnectionNode).lineNumber && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('graph_editor:nodePanel.lineNumber', { defaultValue: 'Line Number' })}</label>
                        <Input
                          value={(formState as ConnectionNode).lineNumber ?? ''}
                          readOnly
                          className="w-full font-mono"
                        />
                      </div>
                    )}

                  </div>
                </div>

                <hr className="border-gray-200 dark:border-dark-border" />

                {/* Confidence Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    <h4 className="font-medium text-gray-900 dark:text-dark-text">
                      {t('data_catalog:graph_editor.details.confidence.title')}
                    </h4>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('data_catalog:graph_editor.details.confidence.detection_confidence')}
                      </span>
                      <div className="flex items-center gap-2">
                        <span
                          className="text-sm font-semibold"
                          style={getConfidenceColor(formState?.confidence || 0)}
                        >
                          {(
                            Math.min(1.0, formState?.confidence || 0) * 100
                          ).toFixed(1)}
                          %
                        </span>
                        <span
                          className="text-xs px-2 py-1 rounded-full bg-opacity-10"
                          style={getConfidenceColor(formState?.confidence || 0)}
                        >
                          {getConfidenceLabel(formState?.confidence || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Associated Connections Section (for Tag nodes) */}
                {formState?.type === NodeType.Tag && currentState && addEdge && (
                  <>
                    <hr className="border-gray-200 dark:border-dark-border" />
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-gray-900 dark:text-dark-text">
                          {t('data_catalog:graph_editor.details.associations.connections_title')}
                        </h4>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowAssociationModal(true)}
                        >
                          {t('data_catalog:graph_editor.details.associations.add_connection')}
                        </Button>
                      </div>

                      <div className="space-y-2">
                        {getRelatedConnections(formState.id).length === 0 ? (
                          <p className="text-sm text-gray-500 dark:text-dark-text-muted">
                            {t('data_catalog:graph_editor.details.associations.no_connections')}
                          </p>
                        ) : (
                          getRelatedConnections(formState.id).map((connection) => (
                            <div
                              key={connection.id}
                              className="flex items-center justify-between p-2 border border-gray-200 dark:border-dark-border rounded-md"
                            >
                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                {t('data_catalog:graph_editor.details.associations.connection_label', { id: connection.id.slice(0, 8) })}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveAssociation(connection.id)}
                              >
                                {t('data_catalog:graph_editor.details.associations.remove')}
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Associated Tags Section (for Connection nodes) */}
                {formState?.type === NodeType.Connection && currentState && addEdge && (
                  <>
                    <hr className="border-gray-200 dark:border-dark-border" />
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-gray-900 dark:text-dark-text">
                          {t('data_catalog:graph_editor.details.associations.tags_title')}
                        </h4>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowAssociationModal(true)}
                        >
                          {t('data_catalog:graph_editor.details.associations.add_tag')}
                        </Button>
                      </div>

                      <div className="space-y-2">
                        {getRelatedTags(formState.id).length === 0 ? (
                          <p className="text-sm text-gray-500 dark:text-dark-text-muted">
                            {t('data_catalog:graph_editor.details.associations.no_tags')}
                          </p>
                        ) : (
                          getRelatedTags(formState.id).map((tag) => (
                            <div
                              key={tag.id}
                              className="flex items-center justify-between p-2 border border-gray-200 dark:border-dark-border rounded-md"
                            >
                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                {tag.content}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveAssociation(tag.id)}
                              >
                                {t('data_catalog:graph_editor.details.associations.remove')}
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        {!showReviewList && (
          <div className="p-4 border-t border-gray-200 dark:border-dark-border shrink-0">
            <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    className="flex-1"
                  >
                    {t('data_catalog:graph_editor.details.buttons.cancel')}
                  </Button>
                  {deleteNode && (
                    <Button
                      variant="destructive"
                      onClick={handleRemove}
                      className="flex-1"
                    >
                      {t('data_catalog:graph_editor.details.buttons.remove')}
                    </Button>
                  )}
                  <Button onClick={handleSave} className="flex-1">
                    {t('data_catalog:graph_editor.details.buttons.approve')}
                  </Button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Association Modal */}
      {showAssociationModal && formState && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center"
          onClick={() => setShowAssociationModal(false)}
        >
          <div
            className="bg-white dark:bg-dark-card rounded-lg shadow-xl w-[500px] max-h-[600px] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-border">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text">
                {formState.type === NodeType.Tag
                  ? t('data_catalog:graph_editor.details.modal.add_connection_title')
                  : t('data_catalog:graph_editor.details.modal.add_tag_title')}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowAssociationModal(false)}
              >
                <XIcon className="h-4 w-4" />
              </Button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Search Box */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
                  <Input
                    type="text"
                    placeholder={
                      formState.type === NodeType.Tag
                        ? t('data_catalog:graph_editor.details.modal.search_connections_placeholder')
                        : t('data_catalog:graph_editor.details.modal.search_tags_placeholder')
                    }
                    value={associationSearchQuery}
                    onChange={(e) => setAssociationSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* List of Available Items */}
              <div className="space-y-2">
                {formState.type === NodeType.Tag ? (
                  getAvailableConnections().length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-dark-text-muted text-center py-4">
                      {associationSearchQuery
                        ? t('data_catalog:graph_editor.details.modal.no_connections_found')
                        : t('data_catalog:graph_editor.details.modal.no_available_connections')}
                    </p>
                  ) : (
                    getAvailableConnections().map((connection) => (
                      <div
                        key={connection.id}
                        className="flex items-center justify-between p-3 border border-gray-200 dark:border-dark-border rounded-md hover:bg-gray-50 dark:hover:bg-dark-muted cursor-pointer"
                        onClick={() => handleAddAssociation(connection.id)}
                      >
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {t('data_catalog:graph_editor.details.associations.connection_label', { id: connection.id.slice(0, 8) })}
                        </span>
                        <Button variant="ghost" size="sm">
                          {t('data_catalog:graph_editor.details.modal.add_button')}
                        </Button>
                      </div>
                    ))
                  )
                ) : (
                  getAvailableTags().length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-dark-text-muted text-center py-4">
                      {associationSearchQuery
                        ? t('data_catalog:graph_editor.details.modal.no_tags_found')
                        : t('data_catalog:graph_editor.details.modal.no_available_tags')}
                    </p>
                  ) : (
                    getAvailableTags().map((tag) => (
                      <div
                        key={tag.id}
                        className="flex items-center justify-between p-3 border border-gray-200 dark:border-dark-border rounded-md hover:bg-gray-50 dark:hover:bg-dark-muted cursor-pointer"
                        onClick={() => handleAddAssociation(tag.id)}
                      >
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {tag.content}
                        </span>
                        <Button variant="ghost" size="sm">
                          {t('data_catalog:graph_editor.details.modal.add_button')}
                        </Button>
                      </div>
                    ))
                  )
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-dark-border">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowAssociationModal(false)}
              >
                {t('data_catalog:graph_editor.details.modal.close_button')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default DetailsFlyout
